import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 relative",
  {
    variants: {
      variant: {
        default: "btn-premium bg-gradient-to-r from-primary via-primary to-primary-glow text-primary-foreground dark:text-black premium-shadow hover:premium-shadow-lg hover:scale-[1.03] active:scale-[0.97] hover:brightness-105",
        destructive: "btn-premium bg-destructive text-destructive-foreground premium-shadow hover:premium-shadow-md hover:brightness-110 hover:scale-[1.02]",
        outline: "border-2 border-input bg-background hover:bg-primary/5 hover:text-primary hover:border-primary/50 hover:shadow-[0_0_20px_rgba(59,130,246,0.2)] dark:hover:shadow-[0_0_30px_rgba(34,197,94,0.4)] transition-all duration-300",
        secondary: "btn-premium bg-gradient-to-r from-secondary via-secondary to-secondary/90 text-secondary-foreground dark:text-black hover:opacity-90 premium-shadow hover:scale-[1.02]",
        ghost: "hover:bg-primary/10 hover:text-primary hover:shadow-[0_0_12px_rgba(59,130,246,0.15)] dark:hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-all duration-300",
        link: "text-primary underline-offset-4 hover:underline hover:brightness-110",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-md px-4 text-xs",
        lg: "h-12 rounded-xl px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
